import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { format } from 'date-fns';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { rbAddReferencePrefix } from 'lib/riskbase';
import { str } from 'lib/str';
import { theme } from 'lib/theme';
import { orderBy as naturalOrderBy } from 'natural-orderby';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL, SET_GENERIC } from 'store/app/actions';
import { selectGeneric } from 'store/app/selectors';
import { doAseLoadPdf, doAseLoadBundleZip } from 'store/assessments/actions';
import {
  selectAseResults,
  selectAseComplianceType,
  selectAseStatus,
  selectAseTemplate,
  selectAseTask,
  selectAseIsEditable,
  selectAseIsOnline,
  selectAseComplianceSubtype,
  selectAseContext,
  selectAseFeatureDownloadBundle,
} from 'store/assessments/selectors';
import { RiskOutcome } from 'store/assessments/types';
import { Txt } from 'components/Txt';
import { FileDisplay } from 'componentsAware/FileDisplay';
import { appContextToFileContext } from 'types/types';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSESSMENT],
  Screens.ASE_SUMMARY
>;

export const SummaryDisplay: React.FC<Props> = ({ navigation }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const showRiskDescriptions = useSelector((state) =>
    selectGeneric(state, 'ase-show-risk-descriptions'),
  );

  const context = useSelector(selectAseContext)!;
  const complianceSubtype = useSelector(selectAseComplianceSubtype);

  const featureDownloadBundle = useSelector(selectAseFeatureDownloadBundle);
  const isOnline = useSelector(selectAseIsOnline);
  const isEditable = useSelector(selectAseIsEditable);
  const isQc = isEditable && isOnline;

  const template = useSelector(selectAseTemplate);
  const { details, reference } = useSelector(selectAseResults);

  const task = useSelector(selectAseTask);

  const status = useSelector(selectAseStatus);
  const isApproved = status.id === 'approved';

  const assessedDateObject = useMemo(
    () => new Date(details.assessedDate),
    [details.assessedDate],
  );

  const assessedDateFormatted = format(assessedDateObject, 'dd MMM yy');
  const validFromDateFormatted = format(
    new Date(details.startDate),
    'dd MMM yy',
  );
  const validUntilDateFormatted = format(
    new Date(details.expiryDate),
    'dd MMM yy',
  );

  const approveDateFormatted = details.approvedDate
    ? format(new Date(details.approvedDate), 'dd MMM yy')
    : null;

  const complianceType = useSelector(selectAseComplianceType);

  const riskRatingReason =
    details.riskChangeReason && !!details.riskReason
      ? details.riskReason
      : null;

  const riskRating: RiskOutcome | null = React.useMemo(() => {
    if (details.risk) {
      return details.risk;
    }
    const { matrix } = complianceType;
    const likelihoodId = details.riskLikelihoodId;
    const severityId = details.riskSeverityId;
    const ratingId = matrix?.outcomes[`${likelihoodId}x${severityId}`];
    if (!matrix || !likelihoodId || !severityId || !ratingId) {
      return null;
    }
    return {
      likelihood: {
        description: matrix.likelihoods[likelihoodId]!.description || '',
        name: matrix.likelihoods[likelihoodId]!.name,
      },
      severity: {
        description: matrix.severities[severityId]!.description || '',
        name: matrix.severities[severityId]!.name,
      },
      rating: {
        colour: matrix.ratings[ratingId]!.bcolour,
        description: matrix.ratings[ratingId]!.description || '',
        name: matrix.ratings[ratingId]!.name,
      },
    };
  }, [
    complianceType,
    details.risk,
    details.riskLikelihoodId,
    details.riskSeverityId,
  ]);

  const agreedScope = details.remarksScope
    ? details.remarksScope
    : template.default_remarks_scope;

  const changesToScope = details.remarksScopeChange
    ? details.remarksScopeChange
    : template.default_remarks_scope_change;

  const handleShowRiskDescriptions = (show: boolean) =>
    dispatch(
      SET_GENERIC({
        key: 'ase-show-risk-descriptions',
        value: show ? true : undefined,
      }),
    );

  const handleEditPress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.AseSummary,
        params: {},
      }),
    );

  return (
    <PageScrollView
      isInModal={false}
      header={
        <NavigationHeader
          heading="Overview"
          onPressBackButton={
            screenSize === 'small' ? navigation.goBack : undefined
          }
          onPressSaveButton={isQc ? handleEditPress : undefined}
          saveButtonText="Edit"
        />
      }
    >
      <ViewWithSeparators
        separatorMargin={theme.paddingHorizontalHalf / 2}
        separatorColor="transparent"
        style={{ marginBottom: theme.paddingVertical }}
      >
        <Card margin={false}>
          <ViewWithSeparators horizontal>
            <CardRow
              icon={ICONS.RBPlectrumSolid}
              label={reference ? rbAddReferencePrefix(reference) : undefined}
              style={{ flex: 1 }}
            >
              {!reference && (
                <Txt>
                  {rbAddReferencePrefix('')}
                  <Txt style={{ color: theme.colors.placeholder }}>000000</Txt>
                </Txt>
              )}
            </CardRow>
            <CardRow
              label={status.name}
              icon={ICONS.CircleCheckLight}
              labelColor={`#${status.colour}`}
              iconColor={`#${status.colour}`}
              style={{ flex: 1 }}
            />
          </ViewWithSeparators>
        </Card>
        <Card margin={false}>
          {isApproved ? (
            <ViewWithSeparators horizontal>
              <CardRow
                label="Approved PDF"
                icon={ICONS.FileCertificateDuotone}
                labelColor={theme.colors.blue}
                iconColor={theme.colors.blue}
                style={{ flex: 1 }}
                showMenuOnPress
                hideMenuButton
                menuOptions={[
                  {
                    text: 'Preview Approved PDF',
                    onPress: () => dispatch(doAseLoadPdf({})),
                  },
                  {
                    text: str.sharedownload('Approved PDF'),
                    onPress: () => dispatch(doAseLoadPdf({ doShare: true })),
                  },
                ]}
              />
              <CardRow
                label="Live PDF"
                icon={ICONS.FileDuotone}
                labelColor={theme.colors.blue}
                iconColor={theme.colors.blue}
                style={{ flex: 1 }}
                showMenuOnPress
                hideMenuButton
                menuOptions={[
                  {
                    text: 'Preview Live PDF',
                    onPress: () => dispatch(doAseLoadPdf({ live: true })),
                  },
                  {
                    text: str.sharedownload('Live PDF'),
                    onPress: () =>
                      dispatch(doAseLoadPdf({ live: true, doShare: true })),
                  },
                ]}
              />
            </ViewWithSeparators>
          ) : (
            <CardRow
              label="Draft PDF"
              icon={ICONS.FileDuotone}
              labelColor={theme.colors.blue}
              iconColor={theme.colors.blue}
              showMenuOnPress
              hideMenuButton
              menuOptions={[
                {
                  text: 'Preview Draft PDF',
                  onPress: () => dispatch(doAseLoadPdf({})),
                },
                {
                  text: str.sharedownload('Draft PDF'),
                  onPress: () => dispatch(doAseLoadPdf({ doShare: true })),
                },
              ]}
            />
          )}
          {featureDownloadBundle && (
            <CardRow
              label={str.sharedownload('Bundle ZIP')}
              icon={ICONS.ArrowDownLight}
              iconColor={theme.colors.blue}
              labelColor={theme.colors.blue}
              onPress={() => dispatch(doAseLoadBundleZip({ doShare: true }))}
            />
          )}
        </Card>
      </ViewWithSeparators>

      {!!task?.description && (
        <Card label="Notes for the Assessor">
          <CardRow label={task.description} labelNumberOfLines={0} />
        </Card>
      )}

      <Card>
        {!!details.customReference && (
          <CardRow
            label="Custom Reference"
            value={details.customReference}
            icon={ICONS.HashtagLight}
          />
        )}
        <CardRow
          label="Performed On"
          value={assessedDateFormatted}
          icon={ICONS.CalendarDayDuotone}
        />
        <CardRow
          label="Validity"
          value={`${validFromDateFormatted} – ${validUntilDateFormatted}`}
          icon={ICONS.CertificateLight}
        />
        {!!approveDateFormatted && (
          <CardRow
            label="Approved"
            valueNumberOfLines={3}
            value={
              `${approveDateFormatted}` +
              `${
                details.approvedBy?.length
                  ? `\nby ${details.approvedBy.map((ab) => ab.name).join(', ')}`
                  : ''
              }`
            }
            icon={ICONS.FileCertificateDuotone}
          />
        )}
      </Card>

      {!!details.authorOrganisation.id && (
        <Card label="Performed By">
          <CardRow label={details.authorOrganisation.name} />
        </Card>
      )}

      <Card label={pluralise(details.assessedBy, 'Author', 'Authors')}>
        {naturalOrderBy(details.assessedBy, (v) => `${v.name} ${v.email}`).map(
          (assessor) => {
            return (
              <CardRow
                key={assessor.email}
                label={`${assessor.name} (${assessor.email})`}
                value={assessor.role || 'Assessor'}
                labelAlignTop
              />
            );
          },
        )}
      </Card>

      {(!!riskRatingReason || riskRating) && (
        <Card
          label="Risk Rating"
          labelLinkText={
            showRiskDescriptions ? 'Hide Descriptions' : 'Show Descriptions'
          }
          onLabelLinkPress={() =>
            handleShowRiskDescriptions(!showRiskDescriptions)
          }
        >
          {!showRiskDescriptions && riskRating && (
            <CardRow label="Likelihood" value={riskRating.likelihood.name} />
          )}
          {!showRiskDescriptions && riskRating && (
            <CardRow label="Severity" value={riskRating.severity.name} />
          )}
          {!showRiskDescriptions && riskRating && (
            <CardRow label="Rating" value={riskRating.rating.name} />
          )}
          {!!showRiskDescriptions && !!riskRating && (
            <CardRow
              labelAlignTop
              label={`Likelihood: ${riskRating.likelihood.name}`}
              value={riskRating.likelihood.description}
            />
          )}
          {!!showRiskDescriptions && !!riskRating && (
            <CardRow
              labelAlignTop
              label={`Severity: ${riskRating.severity.name}`}
              value={riskRating.severity.description}
            />
          )}
          {!!showRiskDescriptions && !!riskRating && (
            <CardRow
              labelAlignTop
              label={`Rating: ${riskRating.rating.name}`}
              value={riskRating.rating.description}
            />
          )}
          {!!riskRatingReason && (
            <CardRow
              labelAlignTop
              label="Reason for Rating"
              value={riskRatingReason}
            />
          )}
        </Card>
      )}

      <Card>
        {!!details.remarksAssessor && (
          <CardRow
            labelAlignTop
            label="Assessor Remarks"
            value={details.remarksAssessor}
          />
        )}
        <CardRow labelAlignTop label="Agreed Scope" value={agreedScope} />
        <CardRow
          labelAlignTop
          label="Changes to Scope"
          value={changesToScope}
        />
      </Card>

      {!!details.fileIds.length && (
        <Card>
          <FileDisplay
            context={appContextToFileContext(context)}
            value={details.fileIds}
          />
        </Card>
      )}

      <Card label="Assessment Template">
        <CardRow label="Name" value={template.name} />
        <CardRow
          label="Title"
          value={template.title || complianceSubtype.name}
        />
      </Card>
    </PageScrollView>
  );
};
